import { FormEvent, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { acceptInvite } from "../lib/api";

/** /invite/:token — the recipient sets their name + password and the
 * account is created. There's no separate "preview" step: the token
 * is validated server-side on accept (a bad/used/expired token comes
 * back as an error here), which keeps the flow to a single round-trip
 * and avoids leaking whether a token ever existed.
 *
 * On success we DON'T auto-sign-in — we send the new user to the
 * login page (with their email prefilled and a confirmation banner)
 * so they sign in once with the password they just set. */
export function AcceptInvite() {
  const { token = "" } = useParams();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const resp = await acceptInvite(token, firstName, lastName, password);
      // Account created — hand off to the sign-in page rather than
      // logging them in automatically.
      navigate("/login", {
        replace: true,
        state: { signedUpEmail: resp.user.email },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not accept invite");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center bg-white p-6 shadow rounded">
          <h1 className="text-xl font-semibold text-red-700">
            Invalid invite link
          </h1>
          <p className="mt-2 text-gray-600 text-sm">
            This link is missing its token. Ask whoever invited you to
            resend it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen flex items-center justify-center px-4 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: "url('/agrilens-background.jpg')" }}
    >
      <div className="absolute inset-0 bg-white/25" aria-hidden="true" />
      <form
        onSubmit={onSubmit}
        className="relative z-10 w-full max-w-md bg-white/95 backdrop-blur-sm shadow-xl rounded-lg p-6 space-y-4"
      >
        <div className="flex justify-center pb-1">
          <img src="/logo-app.png" alt="AgriLens.AI" className="h-16 w-auto" />
        </div>
        <h1 className="text-xl font-semibold text-agrilens-green-dark text-center">
          Accept your invitation
        </h1>
        <p className="text-sm text-gray-600 text-center">
          Set your name and a password to activate your account.
        </p>

        {error && (
          <div className="bg-red-50 text-red-700 text-sm p-2 rounded">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">First name</label>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Last name</label>
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              className="w-full border rounded px-3 py-2"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            Set a password
          </label>
          <input
            type="password"
            value={password}
            minLength={8}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full border rounded px-3 py-2"
          />
          <p className="text-xs text-gray-500 mt-1">At least 8 characters.</p>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-agrilens-green text-white py-2 rounded hover:bg-agrilens-green-dark disabled:opacity-50"
        >
          {submitting ? "Creating account…" : "Create account"}
        </button>
      </form>
    </div>
  );
}
